import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = 'calendar_local_reminder_ids';

/**
 * Device-local scheduling layer for calendar reminders — the precision
 * complement to the backend's Task-driven reminders (calendar-event.service.ts's
 * createReminderTasks / calendar:send-reminder task handler), which is the
 * source of truth and fires regardless of this device's state. This layer
 * ONLY covers reminders configured on the device that created/edited the
 * event; other devices/users rely purely on the backend push. Unlike
 * fcm.service.ts's existing scheduleNotificationAsync call (trigger: null,
 * fire-immediately relay for foreground remote pushes), this uses a real
 * future `trigger.date` — there was no prior delayed-trigger usage in this
 * codebase to extend.
 *
 * Scheduled local notification ids are persisted (keyed by
 * `${eventId}:${minutesBefore}`) so they survive app restarts and can be
 * cancelled individually when an event/reminder is edited or deleted.
 */

type StoredIds = Record<string, string>; // `${eventId}:${minutesBefore}` -> local notification id

async function readStoredIds(): Promise<StoredIds> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error('[CalendarLocalReminders] Failed to read stored ids:', error);
    return {};
  }
}

async function writeStoredIds(ids: StoredIds): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    console.error('[CalendarLocalReminders] Failed to persist stored ids:', error);
  }
}

export interface ReminderEventInput {
  _id: string;
  title: string;
  startDate: string | Date;
  location?: { name?: string } | string;
}

/**
 * Cancels any previously-scheduled local notifications for this event, then
 * schedules one per entry in `minutesBefore` whose fire time is still in the
 * future. Call this on every event create/update (after cancelling first —
 * mirrors the backend's cancel-then-recreate approach) and pass an empty
 * `minutesBefore` array to just clear existing local reminders (e.g. on
 * delete/cancel).
 */
export async function scheduleLocalReminders(event: ReminderEventInput, minutesBefore: number[]): Promise<void> {
  if (Platform.OS === 'web') return; // expo-notifications scheduled triggers aren't supported on web

  await cancelLocalReminders(event._id);

  const startDate = new Date(event.startDate);
  const locationName = typeof event.location === 'string' ? event.location : event.location?.name;
  const ids = await readStoredIds();

  for (const minutes of minutesBefore) {
    const fireAt = new Date(startDate.getTime() - minutes * 60000);
    if (fireAt.getTime() <= Date.now()) continue;

    try {
      const notificationId = await Notifications.scheduleNotificationAsync({
        content: {
          title: 'Upcoming event',
          body: locationName ? `${event.title} — ${locationName}` : event.title,
          data: {
            type: 'calendar_reminder',
            calendarEventId: event._id,
            minutesBefore: minutes,
            title: event.title,
            startDate: startDate.toISOString(),
            location: locationName,
          },
          sound: true,
        },
        trigger: { date: fireAt } as any,
      });
      ids[`${event._id}:${minutes}`] = notificationId;
    } catch (error) {
      console.error(`[CalendarLocalReminders] Failed to schedule local reminder for event ${event._id}:`, error);
    }
  }

  await writeStoredIds(ids);
}

/** Cancels every locally-scheduled reminder for one event (all minutesBefore offsets). */
export async function cancelLocalReminders(eventId: string): Promise<void> {
  if (Platform.OS === 'web') return;

  const ids = await readStoredIds();
  const keysForEvent = Object.keys(ids).filter((key) => key.startsWith(`${eventId}:`));
  if (keysForEvent.length === 0) return;

  for (const key of keysForEvent) {
    try {
      await Notifications.cancelScheduledNotificationAsync(ids[key]);
    } catch (error) {
      // Already fired/cancelled — fine, this runs on every edit not just once.
      console.warn(`[CalendarLocalReminders] Could not cancel local reminder ${key}:`, error);
    }
    delete ids[key];
  }

  await writeStoredIds(ids);
}
